import { useEffect, useId, useCallback, useRef, useMemo } from 'react';
import { LowLevelDrawer, LowLevelDrawerProps } from './LowLevelDrawer';
import { useDrawerProvider } from './DrawerProvider';

export interface DrawerProps
  extends Omit<LowLevelDrawerProps, 'open' | 'hideBackdrop'> {
  open: boolean;
  onClose: () => void;
  anchor?: 'primary' | 'secondary';
  footer?: React.ReactNode;
  /**
   * When set, overrides {@link DrawerProvider} width (e.g. fixed-width form drawers).
   */
  width?: LowLevelDrawerProps['width'];
}

/**
 * A responsive drawer component that integrates with DrawerProvider.
 *
 * Features:
 * - Automatic backdrop and multi-drawer management
 * - Responsive design (full width on mobile, adaptive width on desktop)
 * - Smooth slide and width animations
 * - Smart backdrop click behavior
 * - Can be rendered anywhere within a DrawerProvider tree
 *
 * Usage:
 * ```tsx
 * <DrawerProvider>
 *   <Drawer
 *     open={primaryOpen}
 *     onClose={() => setPrimaryOpen(false)}
 *     title="Primary Drawer"
 *     anchor="primary"
 *   >
 *     <p>Primary content (right side, 95% width when alone, 55% when secondary is open)</p>
 *   </Drawer>
 *   <Drawer
 *     open={secondaryOpen}
 *     onClose={() => setSecondaryOpen(false)}
 *     title="Secondary Drawer"
 *     anchor="secondary"
 *   >
 *     <p>Secondary content (left side, 45% width)</p>
 *   </Drawer>
 * </DrawerProvider>
 * ```
 */
export const Drawer = ({
  open,
  onClose,
  anchor = 'primary',
  width: widthOverride,
  ...props
}: DrawerProps) => {
  const drawerId = useId();
  const {
    registerDrawer,
    unregisterDrawer,
    openDrawer,
    closeDrawer,
    getPrimaryWidth,
    getSecondaryWidth,
  } = useDrawerProvider();

  // Stable reference to onClose to prevent unnecessary re-registrations
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const stableOnClose = useCallback(() => {
    onCloseRef.current();
  }, []);

  // Register this drawer with the provider (only on mount/unmount and anchor change)
  useEffect(() => {
    registerDrawer(drawerId, anchor, stableOnClose);
    return () => unregisterDrawer(drawerId);
  }, [drawerId, anchor, stableOnClose, registerDrawer, unregisterDrawer]);

  // Sync open state with provider (with guards to prevent unnecessary calls)
  useEffect(() => {
    if (open) {
      openDrawer(drawerId);
    } else {
      closeDrawer(drawerId);
    }
  }, [open, drawerId, openDrawer, closeDrawer]);

  // Calculate width based on anchor type (reactive to drawer state changes)
  const width = useMemo(() => {
    if (widthOverride !== undefined) {
      return widthOverride;
    }
    return anchor === 'primary' ? getPrimaryWidth() : getSecondaryWidth();
  }, [anchor, getPrimaryWidth, getSecondaryWidth, widthOverride]);

  return (
    <LowLevelDrawer
      {...props}
      open={open}
      onClose={onClose}
      anchor={anchor}
      width={width}
      hideBackdrop={true} // Provider handles the backdrop
      disableBackdropClick={true} // Provider handles backdrop clicks
    />
  );
};
